"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  Quote,
  Verified,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  SlidersHorizontal,
} from "lucide-react";
import Loader from "@/components/Loader";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchTestimonials } from "@/store/slices/landingSlice";
import { imageUrl } from "../services/BaseUrl";

const avatarGradients = [
  "from-[#8B31FF] to-[#6D28D9]",
  "from-[#2B00A4] to-[#8B31FF]",
  "from-[#8B31FF] to-[#4F11C2]",
  "from-[#4F11C2] to-[#8B31FF]",
  "from-[#8B31FF] to-[#6D28D9]",
  "from-[#0B0F19] to-[#8B31FF]",
];

const TextTestimonialsSection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const { data: testimonials, loading } = useAppSelector(
    (state) => state.landing.testimonials,
  );

  const isDark = mode === "dark";
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const [viewMode, setViewMode] = useState<"carousel" | "grid">("carousel");
  const [isPaused, setIsPaused] = useState(false);

  // Touch & Drag states
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);

  useEffect(() => {
    dispatch(fetchTestimonials());
  }, [dispatch]);

  // Responsive cards per page
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCardsPerPage(1);
      } else if (window.innerWidth < 1024) {
        setCardsPerPage(2);
      } else {
        setCardsPerPage(3);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalCards = testimonials?.length || 0;
  const maxIndex = Math.max(0, totalCards - cardsPerPage);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
  };

  // Autoplay for carousel
  useEffect(() => {
    if (viewMode !== "carousel" || isPaused || totalCards <= cardsPerPage)
      return;
    const interval = setInterval(() => {
      handleNext();
    }, 5500);
    return () => clearInterval(interval);
  }, [viewMode, isPaused, currentIndex, maxIndex, totalCards, cardsPerPage]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) handleNext();
    if (distance < -50) handlePrev();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setIsDragging(false);
    const distance = dragStartX - e.clientX;
    if (distance > 50) handleNext();
    if (distance < -50) handlePrev();
  };

  const getFullUrl = (path: string) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    return `${imageUrl}/${cleanPath}`;
  };

  if (loading) {
    return (
      <section
        className={`relative py-16 sm:py-20 overflow-hidden transition-colors duration-700 ${
          isDark ? "bg-[#0B0F19]" : "bg-slate-50"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center min-h-[300px]">
            <Loader
              loading={loading}
              loadingStates={[
                { text: "Loading client success stories" },
                { text: "Fetching verified testimonials" },
              ]}
              mode={mode}
              className="min-h-[180px]"
            />
          </div>
        </div>
      </section>
    );
  }

  if (!testimonials || testimonials.length === 0) return null;

  const isFewCards = testimonials.length < 3;

  return (
    <section
      className={`relative py-14 sm:py-18 lg:py-20 overflow-x-clip transition-colors duration-700 ${
        isDark ? "bg-[#0B0F19]" : "bg-slate-50"
      }`}
    >
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute top-0 right-0 w-[450px] h-[450px] rounded-full blur-[100px] transition-opacity duration-700 ${
            isDark ? "bg-[#8B31FF]/10" : "bg-[#8B31FF]/5"
          }`}
        />
        <div
          className={`absolute bottom-0 left-0 w-[450px] h-[450px] rounded-full blur-[100px] transition-opacity duration-700 ${
            isDark ? "bg-[#8B31FF]/10" : "bg-[#8B31FF]/5"
          }`}
        />
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            isDark ? "opacity-[0.05]" : "opacity-[0.03]"
          }`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="z-10 relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header with Navigation Controls */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-10 gap-6">
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className={`inline-flex items-center gap-2 px-3.5 py-1 rounded-full border mb-3 transition-all duration-300 ${
                isDark
                  ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#8B31FF]"
                  : "border-[#8B31FF]/20 bg-white/70 text-[#8B31FF] shadow-sm"
              }`}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-[#8B31FF]" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8B31FF]" />
              </span>
              <span className="text-[11px] font-bold tracking-widest uppercase">
                Global Testimonials
              </span>
            </motion.div>

            <h2
              className={`text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Partnering in{" "}
              <span className="bg-clip-text bg-gradient-to-r from-[#8B31FF] via-purple-500 to-[#6d1ec9] text-transparent">
                Digital Excellence
              </span>
            </h2>
            <p
              className={`mt-2 max-w-xl text-sm sm:text-base leading-relaxed ${
                isDark ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Unfiltered insights from leaders who have witnessed the impact of
              our engineering.
            </p>
          </div>

          {/* Controls Bar */}
          {testimonials.length > 1 && (
            <div className="flex items-center gap-2 self-start md:self-end">
              <button
                onClick={() =>
                  setViewMode(viewMode === "carousel" ? "grid" : "carousel")
                }
                className={`p-2.5 rounded-lg border transition-all cursor-pointer ${
                  isDark
                    ? "bg-gray-800/80 hover:bg-gray-700 border-gray-700 text-gray-300"
                    : "bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-sm"
                }`}
                title={
                  viewMode === "carousel"
                    ? "Switch to Grid View"
                    : "Switch to Carousel View"
                }
              >
                {viewMode === "carousel" ? (
                  <LayoutGrid size={18} />
                ) : (
                  <SlidersHorizontal size={18} />
                )}
              </button>

              {viewMode === "carousel" && totalCards > cardsPerPage && (
                <>
                  <button
                    onClick={handlePrev}
                    className={`p-2.5 rounded-lg border transition-all cursor-pointer ${
                      isDark
                        ? "bg-gray-800/80 hover:bg-gray-700 border-gray-700 text-gray-200 hover:text-white"
                        : "bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-sm"
                    }`}
                    aria-label="Previous testimonial"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={handleNext}
                    className={`p-2.5 rounded-lg border transition-all cursor-pointer ${
                      isDark
                        ? "bg-gray-800/80 hover:bg-gray-700 border-gray-700 text-gray-200 hover:text-white"
                        : "bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-sm"
                    }`}
                    aria-label="Next testimonial"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Carousel View / Centered Few Cards ── */}
        {viewMode === "carousel" && !isFewCards ? (
          <div
            className="relative overflow-hidden cursor-grab active:cursor-grabbing select-none pt-4 pb-6 -mt-2 -mb-2 px-1"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          >
            <motion.div
              className="flex gap-6 py-2"
              animate={{
                x: `-${currentIndex * (100 / cardsPerPage)}%`,
              }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
            >
              {testimonials.map((testimonial, index) => {
                const gradientColor =
                  avatarGradients[index % avatarGradients.length];

                return (
                  <div
                    key={testimonial._id || testimonial.id || index}
                    className="flex-shrink-0"
                    style={{
                      width: `calc(${100 / cardsPerPage}% - ${
                        (24 * (cardsPerPage - 1)) / cardsPerPage
                      }px)`,
                    }}
                  >
                    <div
                      className={`group relative h-full rounded-3xl border p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 overflow-hidden ${
                        isDark
                          ? "bg-[#111624]/90 backdrop-blur-xl border-white/10 hover:border-purple-500/50 hover:bg-[#161c2e] shadow-lg hover:shadow-2xl hover:shadow-purple-500/10"
                          : "bg-white border-slate-200/80 shadow-sm hover:border-purple-300 hover:shadow-xl hover:shadow-purple-500/10"
                      } hover:-translate-y-1.5`}
                    >
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#8B31FF] via-purple-500 to-[#6d1ec9] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <div>
                        {/* Top: Stars & Quote */}
                        <div className="flex items-center justify-between mb-5">
                          <div className="flex items-center gap-1 text-amber-400">
                            {[...Array(testimonial.rating || 5)].map((_, i) => (
                              <Star key={i} size={16} fill="currentColor" />
                            ))}
                          </div>
                          <div
                            className={`p-2.5 rounded-lg transition-colors ${
                              isDark
                                ? "bg-white/5 text-purple-300 group-hover:bg-purple-500/20"
                                : "bg-purple-50 text-[#8B31FF] group-hover:bg-purple-100"
                            }`}
                          >
                            <Quote size={16} className="transform rotate-180" />
                          </div>
                        </div>

                        {/* Text */}
                        <p
                          className={`text-sm sm:text-base leading-relaxed mb-6 font-normal ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          &ldquo;{testimonial.text}&rdquo;
                        </p>
                      </div>

                      <div>
                        {/* Project / Result */}
                        {(testimonial.project || testimonial.result) && (
                          <div
                            className={`mb-5 p-3.5 rounded-lg border ${
                              isDark
                                ? "bg-white/[0.03] border-white/5"
                                : "bg-slate-50 border-slate-200/60"
                            }`}
                          >
                            <div className="flex justify-between items-center text-xs gap-3">
                              {testimonial.project && (
                                <div className="flex-1 min-w-0">
                                  <p
                                    className={`font-semibold uppercase tracking-wider text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Initiative
                                  </p>
                                  <p
                                    className={`font-bold truncate ${isDark ? "text-purple-300" : "text-[#8B31FF]"}`}
                                  >
                                    {testimonial.project}
                                  </p>
                                </div>
                              )}
                              {testimonial.result && (
                                <div className="text-right flex-1 min-w-0">
                                  <p
                                    className={`font-semibold uppercase tracking-wider text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Impact
                                  </p>
                                  <p className="font-bold text-emerald-500 dark:text-emerald-400 truncate">
                                    {testimonial.result}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Author */}
                        <div
                          className={`flex items-center gap-3.5 pt-4 border-t ${
                            isDark ? "border-white/10" : "border-slate-100"
                          }`}
                        >
                          {testimonial.imageUrl ? (
                            <div
                              className={`w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border-2 shadow-sm ${
                                isDark ? "border-white/15" : "border-purple-200"
                              }`}
                            >
                              <img
                                src={getFullUrl(testimonial.imageUrl)}
                                alt={testimonial.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display =
                                    "none";
                                }}
                              />
                            </div>
                          ) : (
                            <div
                              className={`w-11 h-11 rounded-full bg-gradient-to-br ${gradientColor} flex items-center justify-center text-white text-sm font-bold shadow-md flex-shrink-0`}
                            >
                              {testimonial.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                          )}

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <h4
                                className={`font-bold text-sm truncate ${
                                  isDark ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {testimonial.name}
                              </h4>
                              <Verified className="w-3.5 h-3.5 text-[#8B31FF] flex-shrink-0" />
                            </div>
                            <p
                              className={`text-xs truncate ${
                                isDark ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {testimonial.role}
                              {testimonial.company
                                ? ` • ${testimonial.company}`
                                : ""}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        ) : (
          /* ── Centered Grid View for Few Cards or User Grid View ── */
          <div
            className={`gap-6 pt-2 pb-4 ${
              isFewCards
                ? "flex flex-wrap justify-center items-stretch max-w-4xl mx-auto"
                : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {testimonials.map((testimonial, index) => {
              const gradientColor =
                avatarGradients[index % avatarGradients.length];

              return (
                <motion.div
                  key={testimonial._id || testimonial.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className={`group relative rounded-3xl border p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 overflow-hidden ${
                    isFewCards ? "w-full max-w-md" : ""
                  } ${
                    isDark
                      ? "bg-[#111624]/90 backdrop-blur-xl border-white/10 hover:border-purple-500/50 hover:bg-[#161c2e] shadow-lg hover:shadow-2xl hover:shadow-purple-500/10"
                      : "bg-white border-slate-200/80 shadow-sm hover:border-purple-300 hover:shadow-xl hover:shadow-purple-500/10"
                  } hover:-translate-y-1.5`}
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#8B31FF] via-purple-500 to-[#6d1ec9] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div>
                    {/* Top: Stars & Quote */}
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-1 text-amber-400">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} size={16} fill="currentColor" />
                        ))}
                      </div>
                      <div
                        className={`p-2.5 rounded-lg transition-colors ${
                          isDark
                            ? "bg-white/5 text-purple-300 group-hover:bg-purple-500/20"
                            : "bg-purple-50 text-[#8B31FF] group-hover:bg-purple-100"
                        }`}
                      >
                        <Quote size={16} className="transform rotate-180" />
                      </div>
                    </div>

                    {/* Text */}
                    <p
                      className={`text-sm sm:text-base leading-relaxed mb-6 font-normal ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      &ldquo;{testimonial.text}&rdquo;
                    </p>
                  </div>

                  <div>
                    {/* Project / Result */}
                    {(testimonial.project || testimonial.result) && (
                      <div
                        className={`mb-5 p-3.5 rounded-lg border ${
                          isDark
                            ? "bg-white/[0.03] border-white/5"
                            : "bg-slate-50 border-slate-200/60"
                        }`}
                      >
                        <div className="flex justify-between items-center text-xs gap-3">
                          {testimonial.project && (
                            <div className="flex-1 min-w-0">
                              <p
                                className={`font-semibold uppercase tracking-wider text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Initiative
                              </p>
                              <p
                                className={`font-bold truncate ${isDark ? "text-purple-300" : "text-[#8B31FF]"}`}
                              >
                                {testimonial.project}
                              </p>
                            </div>
                          )}
                          {testimonial.result && (
                            <div className="text-right flex-1 min-w-0">
                              <p
                                className={`font-semibold uppercase tracking-wider text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Impact
                              </p>
                              <p className="font-bold text-emerald-500 dark:text-emerald-400 truncate">
                                {testimonial.result}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Author */}
                    <div
                      className={`flex items-center gap-3.5 pt-4 border-t ${
                        isDark ? "border-white/10" : "border-slate-100"
                      }`}
                    >
                      {testimonial.imageUrl ? (
                        <div
                          className={`w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border-2 shadow-sm ${
                            isDark ? "border-white/15" : "border-purple-200"
                          }`}
                        >
                          <img
                            src={getFullUrl(testimonial.imageUrl)}
                            alt={testimonial.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          className={`w-11 h-11 rounded-full bg-gradient-to-br ${gradientColor} flex items-center justify-center text-white text-sm font-bold shadow-md flex-shrink-0`}
                        >
                          {testimonial.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <h4
                            className={`font-bold text-sm truncate ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {testimonial.name}
                          </h4>
                          <Verified className="w-3.5 h-3.5 text-[#8B31FF] flex-shrink-0" />
                        </div>
                        <p
                          className={`text-xs truncate ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {testimonial.role}
                          {testimonial.company
                            ? ` • ${testimonial.company}`
                            : ""}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default TextTestimonialsSection;
